/**
 * Typed client for the admin agent API (/admin/agent/*, ikompete-server routers/agent.py),
 * plus the event stream reader. The stream uses fetch, not EventSource, because EventSource
 * cannot send the Bearer header.
 */
import { apiService } from './api';
import config from '../config/environment';
import {
  AgentAuditPage, AgentAuditRow, AgentConversation, AgentConversationSummary, AgentEvent,
  AgentEventType, AgentFileLink, AgentNote, AgentSettingsTestResult, AgentSettingsUpdate,
  AgentSettingsView, AgentStatus,
} from '../types/interfaces';

const BASE = '/admin/agent';

/** Wait before reconnecting a dropped event stream. */
export const RECONNECT_DELAY_MS = 2000;

export const agentApi = {
  async status(): Promise<AgentStatus> {
    return (await apiService.get(`${BASE}/status`)).data;
  },

  async getSettings(): Promise<AgentSettingsView> {
    return (await apiService.get(`${BASE}/settings`)).data;
  },

  async saveSettings(body: AgentSettingsUpdate): Promise<AgentSettingsView> {
    return (await apiService.put(`${BASE}/settings`, body)).data;
  },

  async testSettings(): Promise<AgentSettingsTestResult> {
    return (await apiService.post(`${BASE}/settings/test`)).data;
  },

  async listConversations(archived = false): Promise<AgentConversationSummary[]> {
    return (await apiService.get(`${BASE}/conversations`, { archived })).data;
  },

  async createConversation(title?: string | null): Promise<{ id: number }> {
    return (await apiService.post(`${BASE}/conversations`, { title: title ?? null })).data;
  },

  async updateConversation(id: number, changes: { title?: string; archived?: boolean }): Promise<{ ok: true }> {
    return (await apiService.patch(`${BASE}/conversations/${id}`, changes)).data;
  },

  async getConversation(id: number): Promise<AgentConversation> {
    return (await apiService.get(`${BASE}/conversations/${id}`)).data;
  },

  /** Multipart: `text` plus one `files` field per attachment. */
  async sendMessage(id: number, text: string, files: File[] = []): Promise<{ message_id: number; job_id: number }> {
    const form = new FormData();
    form.append('text', text);
    files.forEach((f) => form.append('files', f, f.name));
    return (await apiService.upload(`${BASE}/conversations/${id}/messages`, form)).data;
  },

  async approvePlan(id: number, fingerprint: string): Promise<{ job_id: number }> {
    return (await apiService.post(`${BASE}/plans/${id}/approve`, { fingerprint })).data;
  },

  async revisePlan(id: number, comment: string): Promise<{ job_id: number }> {
    return (await apiService.post(`${BASE}/plans/${id}/revise`, { comment })).data;
  },

  async cancelPlan(id: number): Promise<{ ok: true }> {
    return (await apiService.post(`${BASE}/plans/${id}/cancel`)).data;
  },

  async listAudit(params: { limit?: number; offset?: number; q?: string } = {}): Promise<AgentAuditPage> {
    return (await apiService.get(`${BASE}/audit`, params)).data;
  },

  async getAudit(id: number): Promise<AgentAuditRow> {
    return (await apiService.get(`${BASE}/audit/${id}`)).data;
  },

  async fileLink(id: number): Promise<AgentFileLink> {
    return (await apiService.get(`${BASE}/files/${id}/link`)).data;
  },

  async listNotes(): Promise<AgentNote[]> {
    return (await apiService.get(`${BASE}/notes`)).data;
  },

  async createNote(text: string, tags: string[] = []): Promise<AgentNote> {
    return (await apiService.post(`${BASE}/notes`, { text, tags })).data;
  },

  async updateNote(id: number, text: string, tags: string[] = []): Promise<AgentNote> {
    return (await apiService.put(`${BASE}/notes/${id}`, { text, tags })).data;
  },

  async deleteNote(id: number): Promise<AgentNote> {
    return (await apiService.delete(`${BASE}/notes/${id}`)).data;
  },

  async refreshKnowledge(): Promise<{ job_id: number }> {
    return (await apiService.post(`${BASE}/knowledge/refresh`)).data;
  },
};

/** Same token lookup as the api.js request interceptor. */
function authToken(): string | null {
  return localStorage.getItem(config.TOKEN_STORAGE_KEY) || sessionStorage.getItem(config.TOKEN_STORAGE_KEY);
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const done = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal.addEventListener('abort', done);
  });
}

/**
 * Parse one SSE frame (the text between blank lines). Returns null for comments such as
 * `: keepalive` and for frames without data.
 */
export function parseSseFrame(frame: string): AgentEvent | null {
  let id: string | null = null;
  let type = 'message';
  const data: string[] = [];
  frame.split('\n').forEach((line) => {
    if (!line || line.startsWith(':')) return;
    const colon = line.indexOf(':');
    const field = colon < 0 ? line : line.slice(0, colon);
    let value = colon < 0 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'id') id = value;
    else if (field === 'event') type = value;
    else if (field === 'data') data.push(value);
  });
  // A frame without an id is dropped on purpose: the server always sends one, and the seq is
  // the resume cursor.
  if (id === null || data.length === 0) return null;
  try {
    return { seq: Number(id), type: type as AgentEventType, payload: JSON.parse(data.join('\n')) };
  } catch {
    return null; // not JSON: skip the frame rather than reconnect in a loop
  }
}

/** Event types the GUI knows; frames of any other type are skipped. */
export const AGENT_EVENT_TYPES: readonly AgentEventType[] = [
  'user_message', 'assistant_message', 'tool_call', 'tool_result', 'plan',
  'status', 'result', 'error', 'note_suggestion', 'done',
];

/** Splits a byte stream into SSE frames, across chunk and UTF-8 boundaries. */
function frameSplitter() {
  const decoder = new TextDecoder();
  let buffer = '';
  let cr = ''; // a trailing "\r" may be the first half of a "\r\n" split across chunks
  return (chunk: Uint8Array): string[] => {
    let text = cr + decoder.decode(chunk, { stream: true });
    cr = text.endsWith('\r') ? '\r' : '';
    if (cr) text = text.slice(0, -1);
    buffer += text.replace(/\r\n?/g, '\n');
    const frames: string[] = [];
    let end = buffer.indexOf('\n\n');
    while (end >= 0) {
      frames.push(buffer.slice(0, end));
      buffer = buffer.slice(end + 2);
      end = buffer.indexOf('\n\n');
    }
    return frames;
  };
}

/**
 * Stream a conversation's events after `after`, calling `onEvent` for each, in seq order.
 *
 * - On a drop (network error, the server closing the stream, a 5xx) it waits `retryDelayMs`
 *   (default 2000 ms) and reconnects with `after` set to the last seq delivered.
 * - Resolves when `signal` aborts.
 * - Rejects with an Error carrying `status` on a 4xx (404: unknown conversation or agent
 *   disabled; 401/403: signed out or not allowed), which a retry would not fix. Callers must
 *   catch this rejection; treat 401 like api.js does (save the page and redirect to /login),
 *   because fetch bypasses the axios interceptor.
 * - Rejects with the callback's error if `onEvent` throws (the stream is closed first).
 *
 * Only events with a safe-integer seq greater than the last one and a known type are delivered.
 */
export async function streamEvents(
  conversationId: number,
  after: number,
  onEvent: (e: AgentEvent) => void,
  signal: AbortSignal,
  retryDelayMs: number = RECONNECT_DELAY_MS,
): Promise<void> {
  let last = after;
  while (!signal.aborted) {
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    try {
      const headers: Record<string, string> = { Accept: 'text/event-stream' };
      const token = authToken();
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(
        `${config.API_BASE_URL}${BASE}/conversations/${conversationId}/events?after=${last}`,
        { headers, signal },
      );
      if (res.status >= 400 && res.status < 500) {
        await res.body?.cancel().catch(() => undefined);
        throw Object.assign(new Error(`Event stream failed (HTTP ${res.status})`), { status: res.status, fatal: true });
      }
      if (res.ok && res.body) reader = res.body.getReader();
    } catch (err) {
      if ((err as { fatal?: boolean })?.fatal) throw err;
      if (signal.aborted) return;
      // Network error: reconnect below.
    }

    if (reader) {
      const split = frameSplitter();
      for (;;) {
        let chunk: ReadableStreamReadResult<Uint8Array>;
        try {
          chunk = await reader.read();
        } catch {
          break; // broken stream (or abort): reconnect below unless aborted
        }
        if (chunk.done || !chunk.value) break;
        // Outside the network try: an onEvent error rejects instead of looking like a drop.
        for (const frame of split(chunk.value)) {
          const event = parseSseFrame(frame);
          if (!event || !Number.isSafeInteger(event.seq) || event.seq <= last
              || !AGENT_EVENT_TYPES.includes(event.type)) continue;
          last = event.seq;
          try {
            onEvent(event);
          } catch (err) {
            await reader.cancel().catch(() => undefined);
            throw err;
          }
        }
      }
    }
    if (!signal.aborted) await wait(retryDelayMs, signal);
  }
}
